import type { SupplierAssessmentStatus, SupplierBaseItem, SupplierListItem } from './organization-types';
import type { SupplierRiskPayload, SupplierRiskTopicOverviewResponse } from '../../mcp-payloads/supplier-risk';

export type SupplierCardItem = Omit<SupplierListItem, 'canDelete'>;
export type SupplierSearchItem = Pick<
  SupplierCardItem,
  'companyId' | 'companyName' | 'websiteURL' | 'internalId' | 'status'
>;

export interface GetSupplierCardResponse {
  buyerCompanyId: string;
  item: SupplierCardItem;
  spendCurrency: string;
  canContinueInChat: boolean;
  /** Whether the card links to the Upstream map: {@link isTracedUpstream}, false until the supplier is assessed. */
  isTracedUpstream: boolean;
  /** Null while the supplier is being modelled or assessed. */
  assessment: { risk: SupplierRiskPayload; overview: SupplierRiskTopicOverviewResponse } | null;
  /** Absent for buyers still on the legacy reader. Preparation does not change assessment status. */
  preparation?: 'ready' | 'pending' | 'failed';
}

/**
 * PLT-7964 — whether the Upstream map traces this supplier: whether a product it sells is mapped to one
 * the taxonomy knows, the rule the map's own read counts a supplier's products by (`tier1ProductShares`).
 * The map offers only the suppliers it traces in its Suppliers filter and drops a pick of any other, so
 * a link for one with nothing mapped would open onto the whole base, unfiltered.
 *
 * Read off the products the Supplier base names (`supplierBaseProducts`) rather than the map's own read,
 * which is far heavier.
 */
export function isTracedUpstream(item: Pick<SupplierBaseItem, 'products'>): boolean {
  return item.products.some(product => product.productCode !== null);
}

export interface GetSupplierSearchResponse {
  suppliers: SupplierSearchItem[];
  hasMore: boolean;
}

export const ON_THE_WAY_SUPPLIER_STATUSES: ReadonlySet<SupplierAssessmentStatus> = new Set([
  'not-modelled',
  'modelling',
  'awaiting-review',
  'assessing',
]);
